import type { LoggerConfiguration } from '@foundation-player/logger';
import { EncryptionType, SourceType } from '@foundation-player/runtime';
import type { PlayerConfig as PublicPlayerConfig } from '@foundation-player/types';

/**
 * For details @see ../../../runtime/src/config/platform.ts
 *
 * @remarks
 *
 * If you add items here, you also need to update  ../../../builder/src/configuration/player-config/add-stus-config.ts
 */
export const DeviceType = {
  /**
   * LG WebOS 3.x (2016-2017)
   */
  WebOS3: 'webos-3',

  /**
   * LG WebOS 4.x (2018-2019)
   */
  WebOS4: 'webos-4',

  /**
   * LG WebOS 5.x (2020)
   */
  WebOS5: 'webos-5',

  /**
   * LG WebOS 6.x (2021)
   */
  WebOS6: 'webos-6',

  /**
   * LG WebOS 22 (2022)
   */
  WebOS22: 'webos-22',

  /**
   * LG WebOS 23 (2023)
   */
  WebOS23: 'webos-23',

  /**
   * Tizen 3.x (2017)
   */
  Tizen3: 'tizen-3.0',

  /**
   * Tizen 4.x (2018)
   */
  Tizen4: 'tizen-4.0',

  /**
   * Tizen 5.x (2019)
   */
  Tizen5: 'tizen-5.0',

  /**
   * Tizen 5.5 (2020)
   */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  Tizen5_5: 'tizen-5.5',

  /**
   * Tizen 6.x (2021)
   */
  Tizen6: 'tizen-6.0',

  /**
   * Tizen 6.5 (2022)
   */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  Tizen6_5: 'tizen-6.5',

  /**
   * Tizen 7.x (2023)
   */
  Tizen7: 'tizen-7.0',

  /**
   * Tizen 8.x (2024)
   */
  Tizen8: 'tizen-8.0',

  /**
   * Sky Q, also known as Amidala or ES160
   */
  SkyQ: 'skyq',

  /**
   * Sky XiOne
   */
  SkyXiOne: 'skyxione',

  /**
   * Sky Stream
   */
  SkyStream: 'skystream',

  /**
   * Magenta G3
   */
  MagentaG3: 'magenta-g3',

  /**
   * Magenta G4
   */
  MagentaG4: 'magenta-g4',

  /**
   * Magenta G5
   */
  MagentaG5: 'magenta-g5',

  /**
   * Vodafone GTV
   */
  VodafoneGTV: 'vodafone-gtv',

  /**
   * Vodafone VTV
   */
  VodafoneVTV: 'vodafone-vtv',

  /**
   * Chromecast
   */
  Chromecast: 'chromecast',

  /**
   * TeleColumbus
   */
  TeleColumbus: 'telecolumbus',

  /**
   * Kepler
   */
  Kepler: 'kepler',
} as const;

export type DeviceType = (typeof DeviceType)[keyof typeof DeviceType];

export type PlayerConfig = PublicPlayerConfig & {
  /**
   * Config for the underlying root player
   *
   * @internal
   */
  rootPlayerConfig?: Record<string, unknown>;

  /**
   * @internal
   */
  loggerConfig?: LoggerConfiguration;

  /**
   * If set to true the player will not check if the browser is supported or not.
   * This will check web apis on the video element as well as DRM support.
   *
   * @internal
   */
  skipSupportDetection?: boolean;

  /**
   * If set to true the player uses just a single element for both content and advertisement,
   * otherwise multiple video elements maybe be used.
   *
   * This is needed for some STBs, which don't support multiple `<video/>` elements.
   *
   * @default false
   * @internal
   */
  forceOneVideoElement?: boolean;

  /**
   * The config for the StUS api. If this is available and enabled, the player can
   * fetch the streaming urls directly from StUS, we only need to provide the movie id or
   * station name.
   */
  streamingURLService?: {
    endpoints: {
      [key in SourceType]?: StreamingURLServiceConfig;
    };
  };

  /**
   * A list of preferred key systems for DRM, ordered by priority
   */
  preferredKeySystems?: EncryptionType[];

  /**
   * Set the id which will identify the player instance. It's not the HTML id used to render the player.
   *
   * It will be reported to NPAW Analytics as "playerid".
   * If it's not set here, a UUIDv4 will be generated instead.
   *
   * Why would i like to do this?
   *
   * Setting the id allows reusing an id from the outside.
   * This way was introduced to reuse the visit id from the SSB,
   * which enables a tester to find a session in NPAW more easily,
   * because it's not necessary to map a visit id to a generated player id.
   *
   * @internal
   */
  playerId?: string;

  /**
   * Due to patent conformance we may have to toggle the usage of the Bola ABR rule if applicable for the used player tech.
   *
   * @default false
   */
  enableBolaAbr?: boolean;
};

export type StreamingURLServiceConfig = {
  endpoint: string;
};
